"use client";

import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { fromTimestamp } from "@/lib/firestore";
import type { CalendarEvents } from "@/lib/models";

interface CreateEventModalProps {
  householdId: string;
  event?: CalendarEvents | null;
  onClose: () => void;
  onSaved?: () => void;
}

function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function CreateEventModal({
  householdId,
  event,
  onClose,
  onSaved,
}: CreateEventModalProps) {
  const { profile } = useHouseholdContext();
  const t = useTranslations();
  const isEdit = !!event;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [allDayDate, setAllDayDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setTitle(event.text || event.summary || "");
      setDescription(event.description || "");
      setLocation(event.location || "");
      setAllDay(Boolean(event.all_day));

      const start = fromTimestamp(event.start);
      if (start) {
        setStartDate(toDateTimeLocal(start));
        const pad = (n: number) => n.toString().padStart(2, "0");
        setAllDayDate(
          `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
        );
      }

      const end = fromTimestamp(event.end);
      if (end) setEndDate(toDateTimeLocal(end));
    } else {
      // Default to next hour for new events
      const now = new Date();
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      setStartDate(toDateTimeLocal(now));

      const endDefault = new Date(now);
      endDefault.setHours(endDefault.getHours() + 1);
      setEndDate(toDateTimeLocal(endDefault));

      const today = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      setAllDayDate(
        `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
      );
      setAllDay(false);
    }
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (allDay && !allDayDate) return;
    if (!allDay && !startDate) return;

    setSaving(true);
    try {
      // For all-day events we anchor to local midnight (start) and the
      // following local midnight (end), matching iOS event semantics.
      let startTs: Timestamp;
      let endTs: Timestamp;
      if (allDay) {
        const [yStr, mStr, dStr] = allDayDate.split("-");
        const y = Number(yStr);
        const m = Number(mStr);
        const d = Number(dStr);
        const startDateObj = new Date(y, m - 1, d, 0, 0, 0, 0);
        const endDateObj = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
        startTs = Timestamp.fromDate(startDateObj);
        endTs = Timestamp.fromDate(endDateObj);
      } else {
        startTs = Timestamp.fromDate(new Date(startDate));
        endTs = endDate
          ? Timestamp.fromDate(new Date(endDate))
          : Timestamp.fromDate(new Date(startDate));
      }

      const data: Record<string, unknown> = {
        text: title.trim(),
        summary: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        start: startTs,
        end: endTs,
        all_day: allDay,
        sync_source: "zoey",
      };

      if (isEdit && event?.uid) {
        const ref = doc(db, `households/${householdId}/events`, event.uid);
        await updateDoc(ref, data);
      } else {
        data.created_by = profile?.uid || "";
        data.created_at = Timestamp.now();
        data.dtstamp = Timestamp.now();
        const ref = collection(db, `households/${householdId}/events`);
        await addDoc(ref, data);
      }

      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving event:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto text-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">
            {isEdit ? t("events.edit.title") : t("events.create.title")}
          </h2>
          <button
            onClick={onClose}
            aria-label={t("events.form.closeAria")}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("events.fields.title.label")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("events.fields.title.placeholder")}
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[color:var(--accent-color)] focus:ring-[color:var(--accent-color)]"
            />
            <span className="font-medium text-gray-700">
              {t("events.fields.allDay.label")}
            </span>
          </label>

          {allDay ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("events.fields.date.label")}
              </label>
              <input
                type="date"
                value={allDayDate}
                onChange={(e) => setAllDayDate(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("events.fields.start.label")}
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("events.fields.end.label")}
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("events.fields.location.label")}
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t("events.fields.location.placeholder")}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("events.fields.description.label")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("events.fields.description.placeholder")}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="w-full py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {saving
              ? t("events.form.saving")
              : isEdit
              ? t("events.edit.submit")
              : t("events.create.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
